import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilSquareIcon, TrashIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import { ivrAPI } from '../../services/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

const IVRPage = () => {
  const [ivrs, setIvrs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, ivr: null });

  useEffect(() => {
    fetchIVRs();
  }, []);

  const fetchIVRs = async () => {
    try {
      const response = await ivrAPI.getAll();
      setIvrs(response.data?.data || response.data || []);
    } catch (error) {
      toast.error('Failed to load IVR menus');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.ivr) return;
    try {
      await ivrAPI.delete(deleteModal.ivr.id || deleteModal.ivr.extension);
      toast.success('IVR deleted');
      setDeleteModal({ open: false, ivr: null });
      fetchIVRs();
    } catch (error) {
      toast.error('Failed to delete IVR');
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Auto-Attendant (IVR)</h1>
          <p className="text-slate-400 mt-1">Create interactive voice menus for callers</p>
        </div>
        <Link to="/ivr/new" className="btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />Create IVR
        </Link>
      </div>

      {ivrs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ivrs.map((ivr) => (
            <div key={ivr.id || ivr.extension} className="card hover:border-primary-500/30 transition-all">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-purple-500/20">
                    <ChatBubbleBottomCenterTextIcon className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{ivr.name}</h3>
                    <p className="text-sm text-primary-400 font-mono">{ivr.extension}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Link to={`/ivr/${ivr.id || ivr.extension}/edit`} className="p-2 text-slate-400 hover:text-primary-400 rounded-lg hover:bg-slate-800">
                    <PencilSquareIcon className="w-4 h-4" />
                  </Link>
                  <button onClick={() => setDeleteModal({ open: true, ivr })} className="p-2 text-slate-400 hover:text-red-400 rounded-lg hover:bg-slate-800">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <ChatBubbleBottomCenterTextIcon className="w-12 h-12 mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No IVR menus</h3>
          <Link to="/ivr/new" className="btn-primary inline-flex"><PlusIcon className="w-5 h-5 mr-2" />Create IVR</Link>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, ivr: null })}
        onConfirm={handleDelete}
        title="Delete IVR"
        message={`Delete "${deleteModal.ivr?.name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default IVRPage;
